import React, { useRef, useLayoutEffect, useEffect, useState } from 'react';
import {
  oneOfType,
  string,
  number,
  arrayOf,
  shape,
  func,
  element,
} from 'prop-types';
import { Chip, Grid, Tooltip } from '@nextiva/next-ui-components';
import clsx from 'clsx';

import { SvgXClose } from 'components/SvgIcon';

export const Chips = ({
  chipList,
  chipClassName,
  labelClassName,
  chipIcon,
}) => {
  return chipList.map((chip) => {
    const handleDelete = () => chip.onDelete(chip.key, chip.data);

    return (
      <Tooltip
        title={
          <div className="Body m-chip-group__chips__tooltip">{chip.label}</div>
        }
        placement="top"
        PopperProps={{
          popperOptions: {
            modifiers: {},
          },
        }}
      >
        <span>
          <Chip
            icon={chipIcon}
            deleteIcon={
              <span
                className="m-chip-group__chips__deleteIcon"
                data-testid="PlatformAdminUI-UsersLink-ChipGroup-DeleteIcon"
              >
                <SvgXClose />
              </span>
            }
            label={
              <div className="m-chip-group__chips__label">
                <div className="Label">
                  <div
                    className={clsx(
                      labelClassName,
                      'm-chip-group__chips__label__text',
                    )}
                    data-testid="PlatformAdminUI-UsersLink-ChipGroup-ChipsLabelText"
                  >
                    {chip.label}
                  </div>
                </div>
              </div>
            }
            className={chipClassName}
            onDelete={handleDelete}
          />
        </span>
      </Tooltip>
    );
  });
};

export const ChipGroup = ({
  chipList,
  className,
  chipClassName,
  labelClassName,
  open,
  showClick,
  maxHeight,
  chipIcon,
}) => {
  const chipsRef = useRef();
  const [chipsScrollHeight, setChipsScrollHeight] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkIsOverflowing = () => {
    if (chipsRef.current) {
      if (chipsRef.current.scrollHeight > maxHeight) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }
  };

  useLayoutEffect(() => {
    if (chipsRef.current) {
      setChipsScrollHeight(chipsRef.current.scrollHeight);

      window.addEventListener('resize', checkIsOverflowing);
    }
  });

  useEffect(() => {
    checkIsOverflowing();
  }, [chipsScrollHeight]);

  let showButtonToggle = '';

  if (isOverflowing) {
    showButtonToggle = (
      <button
        type="button"
        className={clsx('Caption', 'm-chip-group__show-button')}
        onClick={showClick}
        data-testid="PlatformAdminUI-UsersLink-ChipGroup-ShowButton"
      >
        <div
          className="m-chip-group__show-button__show-text"
          data-testid={`PlatformAdminUI-UsersLink-ChipGroup-ShowButtonText-${
            open ? 'Show less' : 'Show all'
          }`}
        >
          {open ? 'Show less' : 'Show all'}
        </div>
      </button>
    );
  } else {
    showButtonToggle = '';
  }

  return (
    <div className={clsx('m-chip-group', className)}>
      <div
        className={clsx(
          'm-chip-group__chips',
          open ? 'm-chip-group__chips--all' : 'm-chip-group__chips--less',
        )}
        ref={chipsRef}
      >
        <Grid item xs={12}>
          <Chips
            chipList={chipList}
            chipClassName={chipClassName}
            labelClassName={labelClassName}
            chipIcon={chipIcon}
          />
        </Grid>
      </div>
      {showButtonToggle}
    </div>
  );
};

ChipGroup.propTypes = {
  chipList: arrayOf(
    shape({
      key: oneOfType([string, number]),
      label: string,
      onDelete: func,
    }),
  ).isRequired,
  chipIcon: element,
  className: string,
  maxHeight: number,
};

ChipGroup.defaultProps = {
  className: '',
  maxHeight: 56,
  chipIcon: null,
};
